'use client';
import { NextPage } from 'next';
import { DashboardMenu } from './components/DashboardMenu';
import { useHash } from '../../../shared/hooks/useHash';
import { createContext, useEffect, useState, PropsWithChildren } from 'react';
import { Footer } from '../../ui/Footer';
import { Drawer } from '../../ui/Drawer';
import { useIsMobile } from '../../../shared/hooks/useIsMobile';
import { usePathname } from 'next/navigation';
import { DashboardHeaderMobile } from './components/DashboardHeaderMobile';
import { DineRewardsManager } from '../../../shared/managers/DineRewards.manager';
import { Helmet } from 'react-helmet';
import { ReduxProps } from '.';
import { Strings } from '../../../shared/constants/Strings';

import styles from './Dashboard.module.scss';

export const AsideMenuContext = createContext({
	isAsideMenuClicked: false,
	setIsAsideMenuClicked: (value: boolean) => {},
});

interface DashboardProps extends ReduxProps {}

export const Dashboard: NextPage<PropsWithChildren<DashboardProps>> = ({
	children,
	userInfo,
	setUserRewardsInfo,
	userRewardsInfo,
}) => {
	const hash = useHash();
	const [isHydrated, setIsHydrated] = useState(false);
	const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
	const isMobile = useIsMobile();
	const pathname = usePathname();
	const isSubpage = (pathname || '').split('/').length > 2;
	const isOpen = !isSubpage && !hash;
	const isMemberProfile = pathname === '/dashboard/member-profile';
	const isHashMenu = hash === 'menu';

	const [isAsideMenuClicked, setIsAsideMenuClicked] = useState(false);
	const value = { isAsideMenuClicked, setIsAsideMenuClicked };

	useEffect(() => {
		const showDrawer = (!isSubpage && !hash) || (isMemberProfile && isHashMenu);
		setMobileDrawerOpen(showDrawer);
	}, [isMemberProfile, isSubpage, hash]);

	useEffect(() => {
		setIsHydrated(true);
	}, []);

	useEffect(() => {
		const fetchAccountInfo = async () => {
			if (userInfo?.email) {
				const response = await DineRewardsManager.GetTransactionHistoryByEmail(userInfo.email);
				setUserRewardsInfo({
					...response,
					userPoints: response?.pointsCount ?? 0,
					userRewardsAmount: response?.totalRewardAmount ?? 0,
					pointsUntilReward: response?.pointsUntilReward ?? 0,
					maximumPointsLimit: response?.maximumPointsLimit ?? 0,
					rewardsWallets: response?.rewardsWallets ?? [],
					userTimeline: response?.timeLine ?? [],
				});
			}
		};
		fetchAccountInfo();
	}, [userInfo]);

	return (
		<AsideMenuContext.Provider value={value}>
			<Helmet>
				<title> {Strings.pageTitle.dashboard} </title>
			</Helmet>
			<div data-testid='dashboard' className={styles['container']}>
				<div data-testid='dashboard-menu' className={styles['menu']}>
					<DashboardMenu userRewardsInfo={userRewardsInfo} setMenuClicked={setIsAsideMenuClicked} />
				</div>

				<div data-testid='dashboard-content' className={styles['content']}>
					<DashboardHeaderMobile isSubpage={isSubpage} setMenuClicked={setIsAsideMenuClicked} />
					<div className={isMobile && isOpen ? styles['noScroll'] : styles['rightContent']}>{children}</div>
				</div>
			</div>
			{isHydrated && isMobile && (
				<Drawer direction='left' isOpen={mobileDrawerOpen} onClose={() => {}} mobileFullScreen>
					<DashboardMenu userRewardsInfo={userRewardsInfo} setMenuClicked={setIsAsideMenuClicked} />
				</Drawer>
			)}

			<Footer />
		</AsideMenuContext.Provider>
	);
};
